import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Notice } from "../../design/components";
import { ApiError } from "../../api/client";
import { useEntities } from "../../api/queries";
import {
  emptyParams,
  missingParams,
  reportBody,
  reportQuery,
  useReportExport,
  useReports,
  useRunReport,
  type ReportDefinition,
  type ReportParams,
} from "../../api/reports";
import { dateTime, omr } from "../../lib/format";
import { saveBlob } from "../../lib/saveBlob";
import { serverText } from "../../i18n/localized";
import styles from "./reports.module.css";

const PARAM_LABEL_KEYS: Record<string, string> = {
  from: "reports.param.from",
  to: "reports.param.to",
  entityIds: "reports.param.entityIds",
  owner: "reports.param.owner",
};

const PARAM_LABEL_DEFAULTS: Record<string, string> = {
  from: "From",
  to: "To",
  entityIds: "Companies",
  owner: "Acts on it",
};

// Server owner values (hub/api/routers/reports.py OWNERS), not the UI's own words.
const OWNER_LABEL_KEYS: Record<string, string> = { entity: "reports.owner.entity", platform: "reports.owner.platform" };
const OWNER_LABEL_DEFAULTS: Record<string, string> = { entity: "Company", platform: "Hub" };

function cell(value: unknown, type: string): string {
  if (value === null || value === undefined) return "—";
  if (type === "amount") return omr(Number(value));
  return String(value);
}

function CompanyPicker({
  chosen,
  onToggle,
}: {
  chosen: string[];
  onToggle: (entityId: string) => void;
}) {
  const { t } = useTranslation();
  const entities = useEntities();
  return (
    <fieldset className={styles.companies}>
      <legend className={styles.label}>{t(PARAM_LABEL_KEYS.entityIds!, PARAM_LABEL_DEFAULTS.entityIds!)}</legend>
      {(entities.data ?? []).map((entity) => (
        <label key={entity.id} className={styles.check}>
          <input
            type="checkbox"
            checked={chosen.includes(entity.id)}
            onChange={() => onToggle(entity.id)}
          />
          {entity.legalName}
        </label>
      ))}
      {entities.data?.length === 0 ? <span className={styles.hint}>{t("reports.page.noCompanies", "No companies yet.")}</span> : null}
    </fieldset>
  );
}

function ReportCard({ definition }: { definition: ReportDefinition }) {
  const { t } = useTranslation();
  const label = (name: string): string => t(PARAM_LABEL_KEYS[name] ?? name, PARAM_LABEL_DEFAULTS[name] ?? name);
  const ownerLabel = (value: string): string => t(OWNER_LABEL_KEYS[value] ?? value, OWNER_LABEL_DEFAULTS[value] ?? value);
  const fieldId = useId();
  const [params, setParams] = useState<ReportParams>(emptyParams());
  const run = useRunReport(definition.key);
  const exportCsv = useReportExport(definition.key);

  const missing = missingParams(definition, params);
  const result = run.data;

  function patch(change: Partial<ReportParams>): void {
    setParams((current) => ({ ...current, ...change }));
    run.reset();
  }

  function toggleEntity(entityId: string): void {
    patch({
      entityIds: params.entityIds.includes(entityId)
        ? params.entityIds.filter((id) => id !== entityId)
        : [...params.entityIds, entityId],
    });
  }

  // The mutation renders its own failure Notice, so a rejection here needs no rethrow.
  function download(): void {
    exportCsv.mutate(reportQuery(definition, params), {
      onSuccess: (blob) =>
        saveBlob(blob, `hub-report-${definition.key}-${params.from}-${params.to}.csv`),
    });
  }

  const rejected = run.error instanceof ApiError && run.error.code === "MISSING_PARAM";
  const rejectedParam = rejected
    ? String((run.error as ApiError & { body?: { param?: string } }).body?.param ?? "")
    : "";

  return (
    <section className="card">
      <div className="card-head wrap">
        <h2 className="grow">{serverText(`reports.catalog.${definition.key}.name`, definition.name)}</h2>
        <span className={styles.tag}>{serverText(`reports.tag.${definition.tag}`, definition.tag)}</span>
        {/* The card's actions sit top-right, clear of the results (BUG-012). */}
        {result ? (
          <button type="button" className="btn btn-sm" disabled={exportCsv.isPending} onClick={download}>
            {t("reports.page.exportCsv", "Export CSV")}
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={missing.length > 0 || run.isPending}
          onClick={() => run.mutate(reportBody(definition, params))}
        >
          {t("reports.page.run", "Run")}
        </button>
      </div>
      <div className="card-body">
        <p className={styles.description}>{serverText(`reports.catalog.${definition.key}.description`, definition.description)}</p>

        <div className={styles.params}>
          {definition.params.map((param) =>
            param.type === "entities" ? (
              <CompanyPicker key={param.name} chosen={params.entityIds} onToggle={toggleEntity} />
            ) : param.type === "select" ? (
              <div key={param.name}>
                <label className={styles.label} htmlFor={`${fieldId}-${param.name}`}>
                  {label(param.name)}
                </label>
                <select
                  id={`${fieldId}-${param.name}`}
                  className="select select-sm"
                  value={params.owner}
                  onChange={(event) => patch({ owner: event.target.value })}
                >
                  <option value="">{t("reports.page.ownerAnyone", "Anyone")}</option>
                  {(param.values ?? []).map((value) => (
                    <option key={value} value={value}>
                      {ownerLabel(value)}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div key={param.name}>
                <label className={styles.label} htmlFor={`${fieldId}-${param.name}`}>
                  {label(param.name)}
                </label>
                <input
                  id={`${fieldId}-${param.name}`}
                  type="date"
                  className="input input-sm"
                  value={params[param.name as "from" | "to"]}
                  onChange={(event) => patch({ [param.name]: event.target.value })}
                />
              </div>
            ),
          )}
        </div>

        {missing.length ? (
          <p className={styles.hint}>
            {t("reports.page.setParamToRun", "Set {{param}} to run this report.", { param: label(missing[0]!) })}
          </p>
        ) : null}

        {run.isError ? (
          <Notice kind="fail">
            {rejectedParam
              ? t("reports.page.reportNeedsParam", "The report needs {{param}}.", { param: label(rejectedParam) })
              : t("reports.page.runError", "The report could not be run.")}
          </Notice>
        ) : null}
        {exportCsv.isError ? <Notice kind="fail">{t("reports.page.exportError", "The export could not be prepared.")}</Notice> : null}

        {result ? (
          <>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    {result.columns.map((column) => (
                      <th key={column.key} className={column.type === "text" ? "" : "r"}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, index) => (
                    <tr key={index}>
                      {result.columns.map((column, position) => (
                        <td key={column.key} className={column.type === "text" ? "" : "r nowrap"}>
                          {cell(row[position], column.type)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.hint}>{t("reports.page.generatedAt", "Generated {{when}}", { when: dateTime(result.generatedAt) })}</p>
          </>
        ) : null}
      </div>
    </section>
  );
}

export function ReportsPage() {
  const { t } = useTranslation();
  const reports = useReports();

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{t("reports.page.title", "Reports")}</h1>
          <p className={styles.lede}>
            {t("reports.page.subtitle", "Group-wide figures over a date range, as a table and as a CSV file.")}
          </p>
        </div>
      </div>

      {reports.isError ? (
        <Notice kind="fail">{t("reports.page.loadError", "The reports could not be loaded.")}</Notice>
      ) : (
        <div className={styles.grid}>
          {(reports.data ?? []).map((definition) => (
            <ReportCard key={definition.key} definition={definition} />
          ))}
        </div>
      )}
    </>
  );
}
